import { supabase } from '../lib/supabase';
import { Game, GameWithPlayers } from '../types';

export const getGames = async (): Promise<GameWithPlayers[]> => {
  console.log('getGames: Starting query...');
  
  // Get current user's player ID
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('Not authenticated');
  }

  // Get player ID for current user
  const { data: playerData, error: playerError } = await supabase
    .from('players')
    .select('id')
    .eq('playerUUID', user.id)
    .single();

  if (playerError || !playerData) {
    console.error('getGames: Error fetching player:', playerError);
    throw new Error('Player not found');
  }

  const currentPlayerId = playerData.id;

  // Fetch games where user is playerA OR playerB
  const { data, error } = await supabase
    .from('games')
    .select(`
      id,
      created_at,
      playerA:players!games_playerA_fkey(*),
      playerB:players!games_playerB_fkey(*),
      framesPlayerA,
      framesPlayerB,
      framesToPlay,
      status
    `)
    .or(`playerA.eq.${currentPlayerId},playerB.eq.${currentPlayerId}`)
    .order('created_at', { ascending: false });
  
  console.log('getGames: Query result:', { data, error });
  
  if (error) {
    console.error('getGames: Supabase error:', error);
    throw error;
  }
  
  const mappedData = (data || []).map(game => ({
    ...game,
    playerA: Array.isArray(game.playerA) ? game.playerA[0] : game.playerA,
    playerB: Array.isArray(game.playerB) ? game.playerB[0] : game.playerB,
  }));
  
  console.log('getGames: Mapped data (filtered to user games):', mappedData);
  return mappedData;
};

export const getGame = async (id: number): Promise<GameWithPlayers> => {
  const { data, error } = await supabase
    .from('games')
    .select(`
      id,
      created_at,
      playerA:players!games_playerA_fkey(*),
      playerB:players!games_playerB_fkey(*),
      framesPlayerA,
      framesPlayerB,
      framesToPlay,
      status
    `)
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return {
    ...data,
    playerA: Array.isArray(data.playerA) ? data.playerA[0] : data.playerA,
    playerB: Array.isArray(data.playerB) ? data.playerB[0] : data.playerB,
  };
};

export const createGame = async (game: {
  playerA: number;
  playerB: number;
  framesToPlay: number;
}): Promise<Game> => {
  const { data, error } = await supabase
    .from('games')
    .insert(game)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updateGame = async (id: number, updates: Partial<Game>): Promise<Game> => {
  const { data, error } = await supabase
    .from('games')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const deleteGame = async (id: number): Promise<void> => {
  try {
    // First, delete all breaks associated with this game
    const { error: breaksError } = await supabase
      .from('breaks')
      .delete()
      .eq('gameId', id);
    
    if (breaksError) {
      console.error('Error deleting breaks:', breaksError);
      throw new Error(`Failed to delete breaks: ${breaksError.message}`);
    }
    
    // Then, delete all frames associated with this game
    const { error: framesError } = await supabase
      .from('frames')
      .delete()
      .eq('gameId', id);
    
    if (framesError) {
      console.error('Error deleting frames:', framesError);
      throw new Error(`Failed to delete frames: ${framesError.message}`);
    }
    
    // Finally, delete the game itself
    const { error: gameError } = await supabase
      .from('games')
      .delete()
      .eq('id', id);
    
    if (gameError) {
      console.error('Error deleting game:', gameError);
      throw new Error(`Failed to delete game: ${gameError.message}`);
    }
    
    console.log(`Successfully deleted game ${id} and all associated data`);
  } catch (error) {
    console.error('Error in deleteGame:', error);
    throw error;
  }
};

